/**
 * Silva Salt — cart bridge.
 *
 * Our custom add-to-cart sections (silva-pdp-buy, silva-product-hero,
 * silva-product-sticky-bar, silva-buy-prompt, silva-salt-comparison,
 * silva-editorial-split) POST to /cart/add.js then dispatch a `cart:refresh`
 * event. Horizon's native drawer does NOT listen for that event — it re-renders
 * only on `cart:update` (see component-cart-items.js #handleCartUpdate, which
 * falls back to sectionRenderer.renderSection when no section HTML is attached).
 *
 * This bridge, loaded once and living on `document`, translates our custom
 * `cart:refresh` into Horizon's `cart:update` so the drawer + header bubble
 * re-render from server truth, then opens the drawer. It survives drawer morphs
 * because it never attaches to morphed nodes.
 *
 * It also delegates one-tap cross-sell adds (`[data-silva-cart-add]`) from
 * inside the drawer, which would otherwise lose their listeners on every morph.
 */
import { CartUpdateEvent } from '@theme/events';

(function () {
  const routes = (window.Theme && window.Theme.routes) || {};
  const CART_URL = routes.cart_url || '/cart';
  const CART_ADD_URL = routes.cart_add_url || '/cart/add.js';

  /** Open the native Horizon cart drawer if present and not already open. */
  function openDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    // Don't re-open an already-open dialog (showModal would throw).
    const dialog = drawer.querySelector('dialog');
    if (dialog && dialog.open) return;
    if (typeof drawer.open === 'function') drawer.open();
  }

  /**
   * Fetch fresh cart state, then dispatch Horizon's cart:update so the drawer
   * and header re-render. Finally open the drawer.
   */
  async function handleRefresh() {
    let cart = null;
    try {
      const res = await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } });
      if (res.ok) cart = await res.json();
    } catch (e) {
      /* Non-fatal: renderSection re-reads from the server regardless. */
    }

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'silva-cart-bridge', {
        source: 'silva-cart-bridge',
        itemCount: cart ? cart.item_count : undefined,
      })
    );

    openDrawer();
  }

  document.addEventListener('cart:refresh', handleRefresh);

  /**
   * One-tap cross-sell add. Delegated so it keeps working after the drawer
   * re-renders. Buttons opt in with:
   *   data-silva-cart-add data-variant-id="123" [data-quantity="1"]
   */
  document.addEventListener('click', function (event) {
    const target = event.target instanceof Element ? event.target.closest('[data-silva-cart-add]') : null;
    if (!target) return;

    const variantId = target.getAttribute('data-variant-id');
    if (!variantId) return;

    event.preventDefault();
    if (target.hasAttribute('aria-disabled') || target.disabled) return;

    const quantity = parseInt(target.getAttribute('data-quantity') || '1', 10) || 1;
    const label = target.querySelector('[data-add-label]') || target;
    const original = label.textContent;

    target.disabled = true;
    label.textContent = 'Adding…';

    fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function () {
        label.textContent = 'Added ✓';
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        // The morph will replace this button; the reset below is a no-op if so.
        setTimeout(function () {
          label.textContent = original;
          target.disabled = false;
        }, 1600);
      })
      .catch(function () {
        label.textContent = original;
        target.disabled = false;
      });
  });
})();
